import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Download, Eye, Loader2, Plus, Trash2 } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableActionsMenu } from "@/components/shared/TableActionsMenu";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { caeKeys } from "@/lib/queryKeys";
import {
  listCaes,
  getCaeXml,
  downloadCae,
  deleteCae,
  type CaeListItem,
} from "@/services/invoiceService";
import { cn } from "@/lib/utils";
import { formatDisplayDate } from "@/lib/dateFormat";
import { AddCaeDialog } from "@/pages/settings/AddCaeDialog";

function getCfeTypeLabel(cfeType: string, t: (key: string) => string): string {
  const key = `invoices.cfeTypes.${cfeType}`;
  const translated = t(key);
  return translated !== key ? translated : `CFE ${cfeType}`;
}

export function SettingsCaeCard(): JSX.Element {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [viewXml, setViewXml] = useState<{
    open: boolean;
    caeId: string | null;
    content: string | null;
    loading: boolean;
  }>({ open: false, caeId: null, content: null, loading: false });
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const { data: caes, isLoading: listLoading } = useQuery({
    queryKey: caeKeys.list(),
    queryFn: listCaes,
  });

  useEffect(() => {
    if (!viewXml.open || !viewXml.caeId || viewXml.content !== null) return;
    setViewXml((prev) => ({ ...prev, loading: true }));
    getCaeXml(viewXml.caeId)
      .then((content) => {
        setViewXml((prev) => ({ ...prev, content, loading: false }));
      })
      .catch(() => {
        toast.error(t("settings.cae.viewXmlError"));
        setViewXml((prev) => ({ ...prev, content: null, loading: false }));
      });
  }, [viewXml.open, viewXml.caeId, viewXml.content, t]);

  const handleViewXml = useCallback((id: string) => {
    setViewXml({ open: true, caeId: id, content: null, loading: true });
  }, []);

  const handleCloseViewXml = useCallback(() => {
    setViewXml({ open: false, caeId: null, content: null, loading: false });
  }, []);

  const handleDownload = useCallback(
    async (id: string) => {
      try {
        await downloadCae(id);
        toast.success(t("settings.cae.downloadSuccess"));
      } catch {
        toast.error(t("settings.cae.downloadError"));
      }
    },
    [t]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await deleteCae(deleteId);
      toast.success(t("settings.cae.deleteSuccess"));
      await queryClient.refetchQueries({
        queryKey: caeKeys.list(),
        type: "active",
      });
      setDeleteId(null);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? "";
      if (typeof msg === "string" && (msg.includes("used") || msg.includes("usado"))) {
        toast.error(t("settings.cae.deleteErrorInUse"));
      } else {
        toast.error(t("settings.cae.deleteError"));
      }
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteId, queryClient, t]);

  const hasExpiringSoon = caes?.some((c) => c.expiringSoon) ?? false;
  const hasRangeLow = caes?.some((c) => c.rangeLow) ?? false;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base text-foreground">{t("settings.cae.title")}</CardTitle>
          <CardDescription>{t("settings.cae.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Top-right add button only when the list has items — the empty
              state renders its own "Agregar CAE", so showing both is redundant. */}
          {caes && caes.length > 0 ? (
            <div className="flex justify-end">
              <Button
                type="button"
                onClick={() => setAddOpen(true)}
                className="transition-colors duration-150 shrink-0"
              >
                <Plus className="mr-2 h-4 w-4 shrink-0" />
                {t("settings.cae.addButton")}
              </Button>
            </div>
          ) : null}
          {hasExpiringSoon && (
            <Alert variant="default" className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
              <AlertDescription>{t("settings.cae.alertsExpiringSoon")}</AlertDescription>
            </Alert>
          )}
          {hasRangeLow && !hasExpiringSoon && (
            <Alert variant="default" className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
              <AlertDescription>{t("settings.cae.alertsRangeLow")}</AlertDescription>
            </Alert>
          )}
          {listLoading ? (
            <div className="flex min-h-[120px] items-center justify-center">
              <LoadingLogo size="lg" />
            </div>
          ) : !caes || caes.length === 0 ? (
            <EmptyState
              title={t("settings.cae.emptyTitle")}
              message={t("settings.cae.emptyMessage")}
              action={
                <Button type="button" onClick={() => setAddOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t("settings.cae.addButton")}
                </Button>
              }
            />
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("settings.cae.emision")}</TableHead>
                    <TableHead>{t("settings.cae.numeroCae")}</TableHead>
                    <TableHead>{t("settings.cae.caeEspecial")}</TableHead>
                    <TableHead>{t("settings.cae.vencimiento")}</TableHead>
                    <TableHead>{t("settings.cae.tipo")}</TableHead>
                    <TableHead>{t("settings.cae.serie")}</TableHead>
                    <TableHead>{t("settings.cae.desde")}</TableHead>
                    <TableHead>{t("settings.cae.hasta")}</TableHead>
                    <TableHead>{t("settings.cae.ultimoUsado")}</TableHead>
                    <TableHead>{t("settings.cae.estado")}</TableHead>
                    <TableHead className="w-[80px]">{t("settings.cae.acciones")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {caes.map((cae: CaeListItem) => {
                    const isDeleted = Boolean(cae.deletedAt);
                    return (
                      <TableRow
                        key={cae.id}
                        className={cn(
                          isDeleted && "opacity-70 bg-muted/20"
                        )}
                      >
                        <TableCell>{formatDisplayDate(cae.validFrom, { locale: i18n.language })}</TableCell>
                        <TableCell>{cae.authorizationNumber ?? "—"}</TableCell>
                        <TableCell>
                          {cae.caeEspecial != null && cae.caeEspecial > 0
                            ? t("settings.cae.caeEspecialYes")
                            : t("settings.cae.caeEspecialNo")}
                        </TableCell>
                        <TableCell>{formatDisplayDate(cae.validTo, { locale: i18n.language })}</TableCell>
                        <TableCell>{getCfeTypeLabel(cae.cfeType, t)}</TableCell>
                        <TableCell>{cae.series}</TableCell>
                        <TableCell>{cae.rangeFrom}</TableCell>
                        <TableCell>{cae.rangeTo}</TableCell>
                        <TableCell>{cae.lastUsedNumber}</TableCell>
                        <TableCell>
                          {isDeleted ? (
                            <Badge variant="secondary">{t("settings.cae.deletedBadge")}</Badge>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          <TableActionsMenu
                            actions={[
                              {
                                label: t("settings.cae.verXml"),
                                icon: Eye,
                                onClick: () => handleViewXml(cae.id),
                              },
                              {
                                label: t("settings.cae.descargar"),
                                icon: Download,
                                onClick: () => handleDownload(cae.id),
                              },
                              ...(isDeleted
                                ? []
                                : [
                                    {
                                      label: t("settings.cae.eliminar"),
                                      icon: Trash2,
                                      onClick: () => setDeleteId(cae.id),
                                      variant: "destructive" as const,
                                    },
                                  ]),
                            ]}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AddCaeDialog open={addOpen} onOpenChange={setAddOpen} />

      <Dialog open={viewXml.open} onOpenChange={(open) => !open && handleCloseViewXml()}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{t("settings.cae.verXml")}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto rounded border bg-muted/30 p-3">
            {viewXml.loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : viewXml.content != null ? (
              <pre className="whitespace-pre-wrap break-all text-xs font-mono">
                {viewXml.content}
              </pre>
            ) : (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {t("settings.cae.viewXmlError")}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={deleteId !== null}
        onOpenChange={(open) => !open && setDeleteId(null)}
        title={t("settings.cae.deleteConfirmTitle")}
        description={t("settings.cae.deleteConfirmMessage")}
        confirmText={t("settings.cae.eliminar")}
        variant="destructive"
        onConfirm={handleDeleteConfirm}
        isLoading={deleteLoading}
      />
    </>
  );
}
